import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Clock, CheckCircle2, XCircle, Bot } from "lucide-react";
import { sharedApi } from "@/lib/api";
import { REWARDS } from "@/constants/testIds";

const STATUS_CLS = {
  approved: "bg-emerald-100 text-emerald-700 border-emerald-200",
  pending: "bg-amber-100 text-amber-700 border-amber-200",
  rejected: "bg-red-100 text-red-700 border-red-200",
};

const STATUS_ICON = {
  approved: CheckCircle2,
  pending: Clock,
  rejected: XCircle,
};

const KIND_LABEL = {
  checkin: "Check-in",
  receipt: "Receipt",
  review: "Review proof",
  redeem: "Redemption",
  redemption: "Redemption",
  // Iter-340 — real points-ledger reasons (rewards hub now reads the
  // same ledger as /points/balance).
  store_purchase: "Store purchase",
  welcome_bonus: "Welcome bonus",
  review_bonus: "Review bonus",
  referrer_bonus: "Referral bonus",
  referee_bonus: "Referral bonus",
  migration: "Balance migration",
  // Iter 630 — admin goodwill grants (rows carry their own `title`;
  // these are the fallback labels only).
  admin_gift: "Gift from AllSale",
  admin_bonus: "Bonus from AllSale",
  admin_gift_reversed: "Gift reversed",
  admin_bonus_reversed: "Bonus reversed",
  // Iter 633 — skip-path service fee refunded as points (rows carry
  // their own `title`; fallback label only).
  service_fee_refund: "Service fee refunded",
};

function labelFor(e) {
  // Iter-343 — real points-ledger rows carry a pre-baked human `title`:
  // render it verbatim when present. Reason/kind mapping stays as the
  // fallback for legacy event rows.
  if (e.title) return e.title;
  const key = e.kind || e.reason;
  if (!key) return "Activity";
  return KIND_LABEL[key] || String(key).replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase());
}

/**
 * Events ledger — every reward action the user has taken, most-recent
 * first. Approved-by-vision events get an "Auto-verified" badge so the
 * customer knows why they got their points so fast.
 */
export default function EventsLedger({ events = [] }) {
  // Iter-456 — history filter chips. Wallet filters server-side via
  // GET /points/history?wallet=; Earned/Spent filters locally by delta
  // sign (the server ignores unknown type values). Falls back to the
  // rewards/me events prop if the history endpoint fails.
  const [wallet, setWallet] = useState("");
  const [kind, setKind] = useState("");

  const historyQ = useQuery({
    queryKey: ["points-history", wallet],
    retry: 0,
    queryFn: async () =>
      (await sharedApi.get("/points/history", { params: wallet ? { wallet } : {} })).data,
  });

  const source = historyQ.data?.items ?? events;
  const filtered = kind
    ? source.filter((e) => {
        const raw = [e.points, e.delta, e.amount].find((v) => typeof v === "number") || 0;
        return kind === "earn" ? raw > 0 : raw < 0;
      })
    : source;

  // Sort newest-first — the backend order isn't a contract we can rely on
  // (iter-38 regression), so we sort defensively without mutating the
  // upstream array/query cache.
  const sorted = [...filtered].sort((a, b) => {
    const ta = new Date(a?.created_at || 0).getTime() || 0;
    const tb = new Date(b?.created_at || 0).getTime() || 0;
    return tb - ta;
  });
  const hasAny = source.length > 0 || wallet || kind;
  if (!hasAny) {
    return (
      <div
        data-testid={REWARDS.ledger}
        className="rounded-2xl bg-white shadow-card p-6 text-center text-sm text-slate-500"
      >
        Your reward history will appear here once you pay at a partner store or check in.
      </div>
    );
  }
  return (
    <section data-testid={REWARDS.ledger} className="rounded-2xl bg-white shadow-card p-5 md:p-6">
      <div className="text-sm font-display font-bold text-slate-900 mb-3">Activity</div>
      <div className="mb-3 flex flex-wrap gap-1.5" data-testid="ledger-filters">
        {[["", "All"], ["nz_classified", "NZ stores"], ["bazaar", "Bazaar"]].map(([v, l]) => (
          <FilterChip key={`w${v}`} active={wallet === v} onClick={() => setWallet(v)} testid={`ledger-filter-wallet-${v || "all"}`}>
            {l}
          </FilterChip>
        ))}
        <span className="w-px self-stretch bg-slate-200 mx-1" />
        {[["", "All activity"], ["earn", "Earned"], ["spend", "Spent"]].map(([v, l]) => (
          <FilterChip key={`k${v}`} active={kind === v} onClick={() => setKind(v)} testid={`ledger-filter-type-${v || "all"}`}>
            {l}
          </FilterChip>
        ))}
      </div>
      {sorted.length === 0 ? (
        <p className="text-sm text-slate-500 py-4 text-center" data-testid="ledger-filter-empty">
          Nothing matches these filters yet.
        </p>
      ) : (
      <ul className="divide-y divide-subtle">
        {sorted.map((e) => {
          // Iter-340 — points-ledger rows are settled facts with no
          // status field: show no badge for them (a bogus "under
          // review" would alarm customers). Event-style rows keep the
          // status pill. `balance_after` is gone from the contract —
          // never referenced here.
          const hasStatus = e.status != null;
          const status = String(e.status || "").toLowerCase();
          const Icon = STATUS_ICON[status] || Clock;
          const badgeCls = STATUS_CLS[status] || STATUS_CLS.pending;
          const isAiApproved = status === "approved" && e.reviewed_by === "ai";
          const displayStatus =
            status === "approved"
              ? "approved"
              : status === "rejected"
              ? "rejected"
              : "under review";
          // Iter-343 — real rows use `delta` (signed); legacy rows use
          // `points` (events) or `amount`.
          const raw = [e.points, e.delta, e.amount].find((v) => typeof v === "number");
          const pts = typeof raw === "number" ? raw : null;
          return (
            <li
              key={e.id}
              data-testid={REWARDS.ledgerItem(e.id)}
              className="py-3 flex items-start justify-between gap-3"
            >
              <div className="min-w-0">
                <div className="text-sm font-semibold text-slate-800 truncate">
                  {labelFor(e)}
                  {e.store_name ? ` · ${e.store_name}` : ""}
                </div>
                <div className="text-[11px] text-slate-500 mt-0.5 flex items-center gap-1.5 flex-wrap">
                  {e.created_at && (
                    <span>{new Date(e.created_at).toLocaleDateString()}</span>
                  )}
                  {isAiApproved && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-[color:var(--brand-blue-wash)] text-[color:var(--brand-blue)] px-1.5 py-0.5 text-[10px] font-bold">
                      <Bot className="w-3 h-3" /> Auto-verified
                    </span>
                  )}
                </div>
              </div>
              <div className="text-right shrink-0">
                {pts != null && pts !== 0 && (
                  <div
                    className={`text-sm font-black ${
                      pts > 0 ? "text-emerald-600" : "text-slate-500"
                    }`}
                  >
                    {pts > 0 ? `+${pts}` : pts} pts
                  </div>
                )}
                {hasStatus && (
                  <span
                    className={`inline-flex items-center gap-1 rounded-full text-[10px] font-bold uppercase tracking-[0.14em] border px-2 py-0.5 mt-1 ${badgeCls}`}
                  >
                    <Icon className="w-3 h-3" /> {displayStatus}
                  </span>
                )}
              </div>
            </li>
          );
        })}
      </ul>
      )}
    </section>
  );
}

function FilterChip({ active, onClick, children, testid }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      className={`rounded-full text-[11px] font-bold px-3 py-1 border transition-colors ${
        active ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-500 border-slate-200 hover:border-slate-400"
      }`}
    >
      {children}
    </button>
  );
}
